'use client';

import { Button, Card, Label, ListGroup, ListGroupItem, Select, ToggleSwitch } from 'flowbite-react';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { HiTrash } from 'react-icons/hi2';
import { useNavigate, useParams } from 'react-router-dom';
import { toast, ToastContentProps } from 'react-toastify';
import { QueryResult } from '../../components/Alerts.tsx';
import { useBuildingContext } from '../../components/BuildingContext.tsx';
import { MultiDatePicker } from '../../components/MultiDatePicker.tsx';
import ProtectedRoute from '../../components/ProtectedRoute';
import RequiresBuilding from '../../components/RequiresBuilding.tsx';
import { useRunOnce } from '../../components/RunOnce.tsx';
import { toastError } from '../../components/Toast.tsx';
import { useAppDispatch, useAppSelector } from '../../lib/hooks.ts';
import { selectDate } from '../../lib/model/date/dateSlice.ts';
import { AanmeldingOptions, Periode, Room, RoomWithUsers, useBatchAanmeldenMutation, useGetUserPreferencesQuery } from '../../lib/model/kantoordagApi.ts';
import { useGetBuildingViewQuery } from '../../lib/model/kantoordag/kantoordagBuildingViewStreamingApi.ts';
import { ROOM__AUTOMATIC_ASSIGNMENT, ROOM__NO_ROOM } from "../Constants.tsx";
import OtherUserSignUp from "../../components/OtherUserSignup.tsx";

export default function Signup() {
    return (
        <RequiresBuilding>
            <SignUpInternal/>
        </RequiresBuilding>
    )
}

function SignUpInternal() {
    const selectedBuilding = useBuildingContext();
    const { t, i18n } = useTranslation("signup");
    const user = useAppSelector(state => state.user.user)
    const date = DateTime.fromISO(useAppSelector(state => state.date.date)) as DateTime<true>
    const params = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch()

    const roomValue = useCallback((room: string): string | undefined => {
        return selectedBuilding.features.hasRooms ? room : undefined
    }, [selectedBuilding])

    const parkingValue = useCallback((park: boolean): boolean | undefined => {
        return selectedBuilding.features.hasParking ? park : undefined
    }, [selectedBuilding])

    const defaultRoomValue = useCallback(() => {
        return roomValue('AutomaticAssignment')
    }, [roomValue])

    const defaultParkingValue = useCallback(() => {
        return parkingValue(true)
    }, [parkingValue])

    // User input
    const [dates, setDates] = useState<Array<DateTime<true>>>([]);
    const [room, setRoom] = useState<string | undefined>(defaultRoomValue());
    const [email, setEmail] = useState<string>(user?.email ?? "")
    const [parkingRequested, setParkingRequested] = useState<boolean | undefined>(defaultParkingValue());
    const [periode, setPeriode] = useState<Periode | undefined>(Periode.HeleDag);

    // APIs
    const preferences = useGetUserPreferencesQuery()
    const [signUp, result] = useBatchAanmeldenMutation()
    const roomQuery = useGetBuildingViewQuery({
        building: selectedBuilding.id,
        date: dates.length > 0 ? dates[0].toISODate() : DateTime.now().toISODate()
    }, {
        pollingInterval: 1000,
        skipPollingIfUnfocused: true
    })

    const resetValues = useCallback(() => {
        setDates([])
        if (preferences.data && preferences.data.signup) {
            const signupPref = preferences.data.signup
            if (signupPref.building && signupPref.building == selectedBuilding.name) {
                if (signupPref.room) {
                    setRoom(roomValue(signupPref.room))
                } else {
                    setRoom(defaultRoomValue())
                }
            } else {
                setRoom(defaultRoomValue())
            }

            if (signupPref.parkingRequested !== undefined && signupPref.parkingRequested !== null) {
                setParkingRequested(parkingValue(signupPref.parkingRequested))
            } else {
                setParkingRequested(defaultParkingValue())
            }

            if (signupPref.periode !== undefined && signupPref.periode !== null) {
                setPeriode(signupPref.periode)
            } else {
                setPeriode(Periode.HeleDag)
            }
        }

    }, [preferences, selectedBuilding, roomValue, parkingValue, defaultRoomValue, defaultParkingValue])

    useRunOnce(() => {
        resetValues();

        const dateParam = DateTime.fromISO(params.date ?? "")
        if (dateParam.isValid) {
            const hasParamDate = dates.some(d => d.equals(dateParam))
            if (!hasParamDate) {
                dispatch(selectDate(dateParam))
                setDates([...dates, dateParam as DateTime<true>])
            }
        }
    })

    function mapError(status: number): string | null {
        if (status == 409) {
            return t("duplicate")
        }
        return null
    }

    interface SignupSuccessProps extends ToastContentProps {
        existingDates?: string[];
    }

    function SignupSuccess({ closeToast, existingDates }: SignupSuccessProps) {
        return (
            <div>
                {t("signup_success")}
                {existingDates && existingDates.length > 0 && (
                    <div className="mt-2">
                        <p>{t("signup_success_already_signed_up_for")}</p>
                        <ul className="list-disc pl-5">
                            {existingDates.map((dateString) => (
                                <li key={dateString}>{DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)}</li>
                            ))}
                        </ul>
                        <br/>
                    </div>
                )}
                {t("signup_success_forwarded_to_overview")}

                <div className={"flex flex-row gap-1"}>
                    <Button onClick={() => closeToast("navigate")} color={"primary"}>{t("navigate")}</Button>
                    <Button onClick={() => closeToast("stay")} color={"secondary"}>{t("stay")}</Button>
                </div>
            </div>
        )
    }

    function handleSignup() {
        const options: AanmeldingOptions = {
            building: selectedBuilding?.name ?? "",
            room: room,
            parkingRequested: parkingRequested,
            periode: periode ?? Periode.HeleDag,
        }

        signUp({
            batchAanmelding: {
                userId: email,
                data: dates.map(d => d.toISODate()),
                options: options
            }
        }).unwrap().then((response) => {
            const firstDate = dates[0]
            resetValues()

            dispatch(selectDate(firstDate))

            toast.success((toastProps) => (
                <SignupSuccess {...toastProps} existingDates={response.existingDates}/>
            ), {
                onClose: (reason: boolean | string | undefined) => {
                    if (!reason || reason === "navigate") {
                        // navigate to overview
                        navigate("/overview/" + firstDate.toISODate())
                    }
                }
            })
        }).catch((error) => {
            if (error.status == 409) {
                toastError({
                    api: result,
                    message: t("duplicate"),
                    errorMapper: mapError
                })
            }
            if (error.status == 400) {
                toastError({
                    api: result,
                    message: t("too_many_signups"),
                    errorMapper: mapError
                })
            } else {
                toastError({
                    api: result,
                    message: t("signup_failed"),
                    errorMapper: mapError
                })
            }
        })
    }

    function removeDate(idx: number) {
        const newDates = [...dates]
        newDates.splice(idx, 1)
        setDates(newDates)
    }

    function onWeekChange(date: DateTime) {
        dispatch(selectDate(date))
    }

    function rooms() {
        const rooms = selectedBuilding?.rooms ?? []
        const roomData = roomQuery.data?.rooms ?? []

        return rooms
            .map(room => roomInfo(room, roomData))
            .filter(room => !room.blocked)
            .map(room => {
                return {
                    name: room.name,
                    available: availableMessage(room.available)
                }
            }) ?? []
    }

    function roomInfo(room: Room, roomData: RoomWithUsers[]) {
        const roomUser = roomData.find(r => r.room === room.name)
        return {
            name: room.name,
            blocked: roomUser?.blocked ?? false,
            available: roomUser?.seatsAvailable ?? -1

        }
    }

    function roomName(roomName: string): string {
        if (roomName == ROOM__NO_ROOM || roomName == ROOM__AUTOMATIC_ASSIGNMENT) {
            return t(roomName)
        }

        return roomName
    }

    const availableStr = t("available")

    function availableMessage(available: number) {
        if (available > 0) {
            return "(" + available + " " + availableStr + ")"
        } else {
            return ""
        }
    }

    function isInPast(dates: Array<DateTime<true>>): boolean {
        return dates
            .filter(d => {
                return d < DateTime.now().startOf('day')
            }).length > 0
    }

    const isValid = dates.length > 0

    return (
        <ProtectedRoute authenticated={true}>
        <div className={"space-y-4"}>
            <h1>{t('signup')}</h1>

            <QueryResult query={roomQuery} message={t("failed_to_retrieve_rooms")}/>

            <OtherUserSignUp email={email} setEmail={setEmail} signUpResult={result}/>

            {/* Multi-date picker */}
            <div className="w-full flex flex-col lg:grid lg:grid-cols-2" data-testid={"signup-options"}>
                <Card className={"overflow-auto lg:mr-2 min-h-96"}>
                    <Label id={"date"}>{t('selectDates')}</Label>
                    <MultiDatePicker
                        language={i18n.language}
                        initial={date}
                        dates={dates}
                        setDates={setDates}
                        onWeekChange={onWeekChange}
                    />

                    {t("selected_dates")}
                    <ListGroup className={"overflow-y-scroll max-h-40 h-full"}>
                        {dates.map((date, idx) => (
                            <ListGroupItem key={idx} className={"relative overflow-visible mr-1"}>
                                <span className={"w-full flex justify-start"}>
                                    {date.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)}
                                </span>

                                <a onClick={() => removeDate(idx)}>
                                    <HiTrash/>
                                </a>
                            </ListGroupItem>
                        ))}
                    </ListGroup>
                </Card>

                <Card className={"flex flex-col gap-1 align-top"}>
                    {t("preferences")}
                    {/* Room selection */}
                    {
                        selectedBuilding.features.hasRooms ?
                            <div className="w-full flex flex-col">
                                <Label>{t('room')}</Label>
                                <Select value={room}
                                        onChange={(e) => setRoom(e.target.value)}
                                >
                                    <option value="">{t('selectRoom')}</option>
                                    {rooms().map((room) => (
                                        <option key={room.name} value={room.name}>{roomName(room.name)} {room.available}</option>
                                    ))}
                                </Select>
                            </div>
                            : null
                    }

                    {/* Parking requested */}
                    {
                        selectedBuilding.features.hasParking ?
                            <div className="flex max-w-md flex-col items-start gap-4">
                                <ToggleSwitch
                                    checked={parkingRequested ?? false}
                                    onChange={(checked) => setParkingRequested(checked)}
                                    label={t('requestParking')}
                                />
                            </div>
                            : null
                    }

                    {/* Period */}
                    <div className="w-full flex flex-col">
                        <Label htmlFor={"periode"}>{t('periode')}</Label>
                        <Select value={periode} id={"periode"}
                                onChange={(e) => setPeriode(e.target.value as Periode)}
                        >
                            <option value="">{t('selectPeriode')}</option>
                            <option value={Periode.HeleDag}>{t('wholeDay')}</option>
                            <option value={Periode.DagDeel}>{t('partDay')}</option>
                        </Select>
                    </div>
                </Card>
            </div>

            {
                dates.length > 1 ?
                    <div className={"text-alfa1-brand-primary-500"}>
                         <Trans t={t}>multiple_dates_availability_incorrect</Trans>
                    </div>
                    :
                    null
            }

            {
                isInPast(dates) ?
                    <div className={"text-alfa1-brand-primary-500"}>
                        <Trans t={t}>date_in_past</Trans>
                    </div>
                    :
                    null
            }

            <Button onClick={handleSignup} color={"primary"} disabled={!isValid}>{t('signup')}</Button>
        </div>
        <span className={"italic"}>{t('defaults_in_preferences')}</span>
        </ProtectedRoute>
);
}
